import { useState, useCallback } from "react";
import { toast } from "sonner";
import { CloudArrowUp, CheckCircle, WarningCircle, XCircle, ArrowClockwise, Prohibit, DownloadSimple } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const BULK_TYPES = ["Gst Rec", "DBK Rec", "RODTEP Rec"];
const TYPE_LABEL = { "Gst Rec": "GST Refund", "DBK Rec": "Drawback (DBK)", "RODTEP Rec": "RODTEP" };

const inr = (v) => "₹" + (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtD = (v) => { const m = String(v || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}-${_MON[+m[2] - 1]}-${m[1].slice(2)}` : (v || "—"); };

const STATUS = {
  ready: { bg: "#DCFCE7", fg: "#166534", label: "Ready", Icon: CheckCircle },
  replace: { bg: "#FEF3C7", fg: "#92400E", label: "Will replace", Icon: ArrowClockwise },
  unchanged: { bg: "#F1F5F9", fg: "#475569", label: "Unchanged (skip)", Icon: Prohibit },
  unmatched: { bg: "#FEE2E2", fg: "#991B1B", label: "Unmatched", Icon: XCircle },
  invalid: { bg: "#FEE2E2", fg: "#991B1B", label: "Invalid", Icon: WarningCircle },
};

const th = { padding: "9px 10px", fontSize: 11, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };

const StatusPill = ({ s }) => {
  const c = STATUS[s] || STATUS.invalid; const Ic = c.Icon;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
      <Ic size={13} weight="fill" /> {c.label}
    </span>
  );
};

export default function BulkReceiptUpload() {
  const [file, setFile] = useState(null);
  const [recType, setRecType] = useState("");
  const [preview, setPreview] = useState(null); // {rows, counts, total, detected_type, amount_header}
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total}
  const [result, setResult] = useState(null);

  const validate = useCallback(async (f, rt) => {
    if (!f) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (rt) fd.append("rec_type", rt);
      const { data } = await api.post("/fa-logistics/bulk-receipts/validate", fd);
      setPreview(data);
      if (!rt && data.rec_type) setRecType(data.rec_type);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the file");
      setPreview(null);
    } finally { setLoading(false); }
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setResult(null);
    validate(f, recType);
  };

  const onType = (rt) => { setRecType(rt); if (file) validate(file, rt); };

  const downloadTemplate = async () => {
    const rt = recType || "RODTEP Rec";
    try {
      const { data } = await api.get(
        `/fa-logistics/bulk-receipts/template?rec_type=${encodeURIComponent(rt)}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rt.replace(/\s+/g, "_").toLowerCase()}_template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template");
    }
  };

  const postToErp = async () => {
    if (!file || !recType) { toast.error("Pick a file and receipt type first"); return; }
    const c = preview?.counts || {};
    const postRows = (preview?.rows || []).filter((r) => r.status === "ready" || r.status === "replace").map((r) => r.row);
    if (!postRows.length) { toast.error("No valid rows to post"); return; }
    let msg = `Post ${postRows.length} receipt(s) to the ERP as "${TYPE_LABEL[recType]}"?`;
    if (c.replace) msg += `\n\n${c.replace} will REPLACE a previously posted receipt (a reversing entry + the new amount).`;
    if (c.unmatched || c.invalid) msg += `\n\n${(c.unmatched || 0) + (c.invalid || 0)} row(s) will be skipped.`;
    if (!window.confirm(msg)) return;
    setPosting(true);
    setResult(null);
    setProgress({ done: 0, total: postRows.length });
    // Start ONE background job: the server uploads + resolves the WHOLE sheet ONCE (no per-chunk
    // re-upload/re-resolution), posts to the ERP with 8 parallel workers, and we poll for live
    // progress. Every idempotency guard runs server-side, so re-running the same sheet is safe.
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("rec_type", recType);
      const { data: start } = await api.post("/fa-logistics/bulk-receipts/commit-job", fd);
      const jobId = start.job_id;
      const total = start.total || 0;
      setProgress({ done: 0, total });
      // Poll the job until it finishes.
      const poll = () => api.get(`/fa-logistics/bulk-receipts/job/${jobId}`).then((r) => r.data);
      let job = await poll();
      while (job.status === "running") {
        setProgress({ done: job.done || 0, total: job.total || total });
        await new Promise((res) => setTimeout(res, 1500));
        job = await poll();
      }
      if (job.status === "error") {
        setResult(null);
        toast.error(`ERP post failed: ${job.error || "unknown error"}`);
      } else {
        const agg = {
          posted: job.posted || 0, replaced: job.replaced || 0,
          skipped: job.skipped || 0, errors: job.errors || [],
        };
        setProgress({ done: job.total || total, total: job.total || total });
        setResult(agg);
        toast.success(`Posted ${agg.posted}, replaced ${agg.replaced}, skipped ${agg.skipped}`);
        // Auto-sync the FA mirror so the Analytics/Realization tiles reflect these receipts
        // immediately — no manual "Refresh" needed. Fire-and-forget (guarded server-side).
        if (agg.posted || agg.replaced) {
          api.post("/fa-logistics/reconcile").then(
            () => toast.success("Syncing realized amounts from ERP — tiles will update shortly"),
            () => {},
          );
        }
      }
      await validate(file, recType); // refresh statuses (posted rows now show "unchanged")
    } catch (e) {
      setResult(null);
      const detail = e.response?.data?.detail;
      toast.error(detail || "ERP post failed");
    } finally {
      setPosting(false);
      setProgress(null);
    }
  };

  const counts = preview?.counts || {};
  const postable = (counts.ready || 0) + (counts.replace || 0);

  return (
    <Layout>
      <div style={{ maxWidth: 1800, margin: "0 auto", padding: "0 4px" }} data-testid="bulk-receipt-page">
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>Bulk Receipt Upload</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            Upload a GST refund / DBK / RODTEP "received" sheet — RODTEP rows match by <b>Shipping Bill No</b>,
            GST &amp; DBK by <b>Export Invoice No</b> — you confirm, then it posts as receipts (same as the manual
            receipt process). Use <b>Download template</b> for the exact columns per type.
          </p>
        </div>

        {/* Controls */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>Receipt Type</div>
            <select data-testid="bulk-type-select" value={recType} onChange={(e) => onType(e.target.value)}
              style={{ padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, minWidth: 200, background: "#fff" }}>
              <option value="">Auto-detect from file…</option>
              {BULK_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>Excel File</div>
            <label data-testid="bulk-file-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "1px dashed #94A3B8", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#334155", background: "#F8FAFC" }}>
              <CloudArrowUp size={18} /> {file ? file.name : "Choose .xlsx…"}
              <input data-testid="bulk-file-input" type="file" accept=".xlsx" onChange={onFile} style={{ display: "none" }} />
            </label>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>Template</div>
            <button data-testid="bulk-template-btn" onClick={downloadTemplate}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 14px", border: "1px solid #CBD5E1", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#334155", background: "#fff" }}>
              <DownloadSimple size={17} /> Download template
            </button>
          </div>
          {preview?.amount_header && (
            <div style={{ fontSize: 12, color: "#64748B" }}>
              Amount column: <b>{preview.amount_header}</b>{preview.detected_type ? ` → ${TYPE_LABEL[preview.detected_type]}` : ""}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button data-testid="bulk-post-btn" onClick={postToErp} disabled={posting || loading || !postable || !recType}
            className="jde-btn jde-btn-primary" style={{ opacity: (!postable || !recType) ? 0.5 : 1 }}>
            {posting ? (progress ? `Posting ${progress.done}/${progress.total}…` : "Posting to ERP…") : `Post to ERP (${postable})`}
          </button>
        </div>

        {/* Progress bar while posting to ERP (posts in chunks) */}
        {posting && progress && (
          <div data-testid="bulk-progress" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#334155", marginBottom: 5 }}>
              <span>Posting receipts to ERP…</span>
              <span style={{ fontWeight: 700 }}>{progress.done} / {progress.total}</span>
            </div>
            <div style={{ height: 10, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
                background: "linear-gradient(90deg,#0EA5E9,#22C55E)", borderRadius: 999, transition: "width .3s ease" }} />
            </div>
          </div>
        )}

        {/* Summary counts */}
        {preview && (
          <div data-testid="bulk-counts" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            {["ready", "replace", "unchanged", "unmatched", "invalid"].map((k) => (
              <div key={k} style={{ background: STATUS[k].bg, color: STATUS[k].fg, padding: "6px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 700 }}>
                {STATUS[k].label}: {counts[k] || 0}
              </div>
            ))}
            <div style={{ padding: "6px 12px", fontSize: 12.5, color: "#64748B" }}>Total: {preview.total}</div>
          </div>
        )}

        {result && (
          <div data-testid="bulk-result" style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13, color: "#065F46" }}>
            <b>Posted {result.posted}</b> · replaced {result.replaced} · skipped {result.skipped}
            {result.errors?.length ? ` · ${result.errors.length} error(s)` : ""}
          </div>
        )}

        {/* Preview table */}
        {loading && <div style={{ padding: 30, textAlign: "center", color: "#64748B" }}>Reading & matching…</div>}
        {preview && !loading && (
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="bulk-preview-table">
              <thead>
                <tr style={{ background: "#0F172A" }}>
                  <th style={th}>#</th>
                  <th style={th}>{recType === "RODTEP Rec" ? "Shipping Bill No" : "Export Invoice"}</th>
                  <th style={th}>Invoice #</th>
                  <th style={th}>ERP Sale No</th>
                  <th style={th}>Party</th>
                  <th style={{ ...th, textAlign: "right" }}>Amount (INR)</th>
                  <th style={th}>Received</th>
                  <th style={th}>Status</th>
                  <th style={th}>Note</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} data-testid={`bulk-row-${r.row}`}>
                    <td style={{ ...td, color: "#94A3B8" }}>{r.row}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{recType === "RODTEP Rec" ? (r.shipping_bill_no || "—") : r.export_invoice_no}</td>
                    <td style={td}>{r.invoice_no || "—"}</td>
                    <td style={td}>{r.sale_no || "—"}</td>
                    <td style={td}>{r.party || "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{inr(r.amount)}</td>
                    <td style={td}>{fmtD(r.rec_date)}</td>
                    <td style={td}><StatusPill s={r.status} /></td>
                    <td style={{ ...td, color: "#64748B", fontSize: 12 }}>
                      {r.status === "replace" ? `was ${inr(r.prev_amount)}` : (r.reason || "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!preview && !loading && (
          <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", background: "#fff", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
            <CloudArrowUp size={40} weight="thin" style={{ marginBottom: 8 }} />
            <div>Choose a receipt sheet to begin, or <b>Download template</b> above. Columns: <b>RODTEP</b> → Shipping Bill No · Amount · Received Date; <b>GST / DBK</b> → Export Invoice No · Amount · Received Date.</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
